import React from 'react';
import { inherits } from 'util';
import { GLOBAL, GlobalDispatcher } from '../App';
import { Navigation } from '../components/interface/Navigation';
import { TopPage } from '../pages/TopPage';
import { mergeClassNames } from '../utils/mergeClassNames';
import KTween from './KTween';
import { Page } from './Page';
import { Pages } from './Pages';

interface RouterProps{
	content:any,
}

export class Router extends React.Component<RouterProps>
{

	private _base:string = '';
	private _paths:{path:string, data:any}[] = [];

	public currentContent:any;
	public currentPath:any;

	private _scrollInit = 0;
	private _scrollTo = 0;
	private _scrollTarget = null;

	state = {
		content: {},
		id: '',
		title: '',
		path: '',
		hash: '',
	}

	get base()
	{
		return this._base;
	}

	get scrollTo()
	{
		return this._scrollTo;
	}

	set scrollTo(value)
	{
		this._scrollTo = value;
		if(this._scrollTarget)
		{
			let target = document.querySelector(this._scrollTarget);
			if(target)
			{
				let y = target.getBoundingClientRect().top + window.scrollY;
	
				window.scrollTo(window.scrollX, ((y - this._scrollInit) * value + this._scrollInit) >> 0);
			}
		}
	}

	get hash()
	{
		return this.state.hash;
	}

	componentDidMount()
	{
		GLOBAL.router = this;
		this._init();

		window.addEventListener('popstate', this._routeChange);
		window.addEventListener('scroll', this._onScroll);
		window.addEventListener('hashchange', this._hashChange);
		this._routeChange();
	}

	componentWillUnmount()
	{
		window.removeEventListener('popstate', this._routeChange);
		window.removeEventListener('scroll', this._onScroll);
		window.removeEventListener('hashchange', this._hashChange);

	}

	private _hashChange = (e) =>
	{
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();
	}

	private _init()
	{
		let _base = document.querySelector('base');
		let base = '/';
		if(_base) base = _base.getAttribute('href');
		this._base = '/';
		this._base = this._normalizeRoute(base);

		let paths = [];
		this._paths = Object.entries(this.props.content.routes).map(([k, v]) => {
			return {path: this._normalizeRoute(k), data: v};
		});
		this._paths.sort(this._sortPaths).reverse();

	}

	private _onScroll = () =>
	{
		let anchors = document.querySelectorAll('[data-anchor]');
		let sy = window.innerHeight * 0.1;
		let foundAnchor;

		let isWhite = true;
		for(let i = 0; i < anchors.length; i++)
		{
			let e = anchors[i];
			let b = e.getBoundingClientRect();
			if(b.bottom > sy && b.top < sy)
			{
				foundAnchor = e.getAttribute('data-anchor');
				if(/.*?theme\-([^\s\_]+).*?$/.test(e.className))
				{
					isWhite = e.className.replace(/.*?theme\-([^\s\_]+).*?$/, '$1') == 'white';
				}
				break;
			}
		}
		document.body.classList.toggle('is-white', isWhite);

		if(foundAnchor)
		{
			this._replaceHash(foundAnchor);
		}
	}

	private _replaceHash(hash)
	{
		if(hash == this.state.hash) return;
		this.state.hash = hash;
		window.history.replaceState({}, this.state.title, this._base.replace(/\/+$/, '') + this.state.path + (this.state.hash.length > 0?'#' + this.state.hash:''));
		GlobalDispatcher.trigger('hash_change');
	}

	goto(path:string)
	{
		if(this.exists(path))
		{
			let title = document.querySelector('title')?.innerHTML || '';

			this.state.title = title;
			this.state.path = path;
			window.history.pushState({}, this.state.title, this.state.path);
			this._routeChange();
		}
	}

	exists(path:string):boolean
	{
		if(!path) path = '';
		if(/^[^\/]*\:/.test(path)) return false;
		path = this._normalizeRoute(path);
		let foundPath = this._paths.find((v) => {
			return path.startsWith(v.path);
		});

		if(foundPath)
		{
			return true;
		}

		return false;
	}

	private _normalizeRoute(path, trailingSlash = true, leadingSlash = true)
	{
		let initPath = path;
		if(!path) return path;
		let hash = null;

		if(/\#/.test(path))
		{
			hash = path.replace(/^.*?\#(.*?)$/, '$1');
		}
		path = path.replace(/^(.*?)\#.*?$/, '$1');
		path = path.replace(/^\/*(.*?)\/*$/, '$1');
		if(/^[^\/].*?\/{2}/.test(path)) return initPath;
		path = '/' + path + '/';

		if(path.startsWith(this._base))
		{
			path = path.replace(this._base, '');
		}
		path = path.replace(/^\/*(.*?)\/*$/, '$1');
		if(trailingSlash)
		{
			path = path.replace(/\/*$/, '/');
		}
		if(leadingSlash)
		{
			path = path.replace(/^\/*/, '/');
		}
		path = path.replace(/\/+/g, '/');
		return path + (hash?'#' + hash:'');
	}

	private _sortPaths(a:{path:string}, b:{path:string}):number
	{
		if(!a.path || !b.path) return 0;
		let pa = a.path.split('/').length;
		let pb = b.path.split('/').length;
		let la = a.path.length;
		let lb = b.path.length;
		if(pa > pb) return 1;
		if(pb < pa) return -1;
		if(la > lb) return 1;
		if(la < lb) return -1;
		return 0;
	}

	scrollToElement(element:HTMLElement|string)
	{
		if(typeof(element) == 'string') element = document.querySelector(element) as HTMLElement;
		if(!element) return;

		let b = element.getBoundingClientRect();
		let y = b.top + window.scrollY;
		window.scrollTo(0, y);
	}


	private _routeChange = () =>
	{
		if(this._paths.length == 0) return;
		let sy = window.scrollY;
		let path = this._normalizeRoute(window.location.pathname + window.location.hash);
		let hash = window.location.hash;
		if(/\#/.test(path))
		{
			hash = path.replace(/^.*?\#(.*?)$/, '$1');
			path = path.replace(/^(.*?)\#.*?$/, '$1');
		}
		let pathParts = path.split('/');
		let foundPath;
		while(!foundPath)
		{
			path = pathParts.join('/');
			foundPath = this._paths.find((v) => {
				return path.startsWith(v.path);
			});
			pathParts.pop();
			if(pathParts.length == 0) return;
		}
		if(!foundPath) foundPath = this._paths[0];

		this.scrollToElement('body');
		this.currentPath = foundPath['path'];
		this.currentContent = this.props.content.content[foundPath.data['content']];
		this.setState({
			path: foundPath['path'],
			id: foundPath.data['content'],
			content: this.props.content.content[foundPath.data['content']],
			hash: '',
		});

		KTween.remove(this);
		if(hash && hash.length > 0)
		{
			this._scrollTarget = `[data-anchor="${hash}"]`;
			this._scrollInit = sy;
			this._scrollTo = 0;
			KTween.tween(this, {scrollTo: 1}, 'easeInOutQuart', 0.3);
			GlobalDispatcher.trigger('hash_change');
		}

		GlobalDispatcher.trigger('route_change');

	}

	public normalize(path)
	{
		path = this._normalizeRoute(path);
		path = this._base + '/' + path;
		path = path.replace(/\/+/g, '/').replace(/^\//, '/');
		return path;
	}

	render()
	{
		return (
			<>
			{
				this.state.content?.['pages']?.map((page, i) => {
					let PageClass = Pages[page['type']];
					if(!PageClass) PageClass = Page;

					return (<section key={i} {...page['props']} className={mergeClassNames([`page-${this.state.id}`, page['props']?.['className'], 'page'], PageClass['styles'])}>
						<PageClass {...page}></PageClass>
					</section>)
				})
			}
			<Navigation content={this.state.content?.['pages']}></Navigation>
			</>
		);
	}
}
